<template>
    <slot />
</template>

<script setup>
    import { Mesh } from 'three';
    import { computed, onBeforeUnmount, ref, watchEffect } from 'vue';

    import { simplePlaneGeometry } from '@resn/gozer-three';
    import { basePaneOptions, usePane, useRafBool, useThreeObject } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import glslFill from '@/libs/lygia/draw/fill.glsl';
    import glslRect from '@/libs/lygia/draw/rect.glsl';
    import glslRectSDF from '@/libs/lygia/sdf/rectSDF.glsl';
    import glslRotate from '@/libs/lygia/space/rotate.glsl';
    import CustomMaterial from '~/components/gl/materials/custom-material';
    import { createGradient } from '~/components/gl/shaders/ShaderChunks/create-gradient';
    import {
        uniforms as matcapUniforms,
        shaderChunks as ShaderChunksMatcap,
    } from '~/components/gl/shaders/ShaderChunks/matcap';
    import { useModelExporter } from '~/composables/useModelExporter';
    import { useGlobalAssets } from '~/providers/GlobalAssets';
    import { useMatcap } from '~/providers/MatcapProvider';

    const props = defineProps({
        layer: {
            type: Number,
            default: 0,
        },
        visible: {
            type: Boolean,
            default: false,
        },
    });

    const v = computed(() => props.visible);
    const active = ref(true);

    const uRotation = ref(-0.5);
    const uWaveHeight = ref(0.05);
    const uWaveSpeed = ref(1);
    const uWaveLength = ref(3.0);
    const uEdgeFalloff = ref(0.2);
    const uNumWaves = ref(12);
    const uWaveSpacing = ref(0.08);
    const uWaveThickness = ref(0.04);
    const uWaveStepAmount = ref(10.0);
    const uWaveStepOffset = ref(0.1);

    const material = new CustomMaterial({
        uniforms: {
            uTime: 0,
            uRotation: 0,
            uWaveHeight: 0,
            uWaveSpeed: 0,
            uWaveLength: 0,
            uEdgeFalloff: 0,
            uNumWaves: 0,
            uWaveSpacing: 0,
            uWaveThickness: 0,
            uWaveStepAmount: 0,
            uWaveStepOffset: 0,

            ...matcapUniforms(),
        },
        vs: /* glsl */ `
            varying vec2 vUv;
            varying vec3 vNormal;
            varying vec3 vMvPosition;

            void main() {
                vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                gl_Position = projectionMatrix * mvPosition;
                
                vUv = uv;
                vMvPosition = -mvPosition.xyz;
                vNormal = normalize(normalMatrix * normal); // view-space normal
            }
        `,
        fs: /* glsl */ `
            uniform float uTime;
            uniform float uRotation;
            uniform float uWaveHeight;
            uniform float uWaveSpeed;
            uniform float uWaveLength;
            uniform float uEdgeFalloff;
            uniform int uNumWaves;
            uniform float uWaveSpacing;
            uniform float uWaveThickness;
            uniform float uWaveStepAmount;
            uniform float uWaveStepOffset;

            varying vec2 vUv;
            varying vec3 vNormal;
            varying vec3 vMvPosition;

            #define NUM_WAVES 20

            ${glslRotate}
            ${ShaderChunksMatcap.funcs}
            ${ShaderChunksMatcap.uniforms}

            void main() {
                vec3 normal = normalize(vNormal);

                vec2 uv = vUv;
                uv = rotate(uv, uRotation);
                
                
                float waveStep = floor(uv.x * uWaveStepAmount) / uWaveStepAmount;
                // float timeOffset = uTime * uWaveSpeed;
                float timeOffset = uTime * uWaveSpeed + waveStep * 3.0;
                float waveUv = sin(uv.x * uWaveLength + timeOffset) * uWaveHeight + uWaveHeight;
                uv.y += waveUv;
                // uv.y += waveStep * uWaveStepOffset;
                
                float distxFromCenter = abs(uv.x * 2.0 - 1.0);
                float waveThickness = mix(uWaveThickness, uWaveThickness * uEdgeFalloff, distxFromCenter);
                
                float waveShape = 0.0;
                float halfCount = (float(uNumWaves) - 1.0) * 0.5;

                for (int i = 0; i < NUM_WAVES; i++) {
                    if (i >= uNumWaves) break;
                    float idx = float(i) - halfCount;
                    float waveStart = 0.5 + idx * uWaveSpacing;
                    float s = step(waveStart, uv.y) * step(uv.y, waveStart + waveThickness);
                    waveShape = max(waveShape, s);
                }

                vec3 color = vec3(1.0);
                vec3 cMatcap = matcap(tMatcap, normal, vMvPosition, false, 1.0, 1.0, 1.0, u_matcapRotation);

                gl_FragColor = vec4(cMatcap, waveShape);
                
                #include <tonemapping_fragment>
                #include <colorspace_fragment>
            }
        `,
        options: {
            name: 'SuperBg',
            depthWrite: false,
        },
    });

    const mesh = new Mesh(simplePlaneGeometry, material);

    useThreeObject(mesh, { name: 'SuperBg', props: { v } });

    mesh.position.z = -0.2;
    mesh.scale.setScalar(1);
    mesh.layers.set(props.layer);
    mesh.renderOrder = -1;

    const { rotation } = useMatcap();

    useGlobalAssets(({ data }) => {
        material.tMatcap = data.matcapIrridescent3;
    });

    let tl = null;
    const show = ({ delay = 0 } = {}) => {
        tl?.kill();

        tl = gsap.timeline({
            delay,
            onStart: () => (active.value = true),
        });
    };

    const hide = ({ delay = 0, _swirl = true } = {}) => {
        tl?.kill();

        tl = gsap.timeline({
            delay,
            onStart: () => (active.value = true),
            onComplete: () => (active.value = false),
        });
    };

    useRafBool(v, ({ timestamp }) => {
        material.uTime = timestamp * 0.001;
        material.u_matcapRotation = rotation.value;
    });

    const destroy = () => {
        material.dispose();
    };

    watchEffect(() => {
        material.uRotation = uRotation.value;
        material.uWaveHeight = uWaveHeight.value;
        material.uWaveSpeed = uWaveSpeed.value;
        material.uWaveLength = uWaveLength.value;
        material.uEdgeFalloff = uEdgeFalloff.value;
        material.uNumWaves = uNumWaves.value;
        material.uWaveSpacing = uWaveSpacing.value;
        material.uWaveThickness = uWaveThickness.value;
        material.uWaveStepAmount = uWaveStepAmount.value;
        material.uWaveStepOffset = uWaveStepOffset.value;
    });

    onBeforeUnmount(destroy);

    defineExpose({ show, hide });

    const modelExport = useModelExporter();

    const showPane = false;
    if (!modelExport && showPane) {
        usePane(
            [
                { name: 'uRotation', value: uRotation, options: { min: -3, max: 3, step: 0.01 } },
                {
                    name: 'uWaveHeight',
                    value: uWaveHeight,
                    options: { min: 0, max: 0.2, step: 0.001 },
                },
                { name: 'uWaveSpeed', value: uWaveSpeed, options: basePaneOptions },
                {
                    name: 'uWaveLength',
                    value: uWaveLength,
                    options: { min: 0, max: 10, step: 0.01 },
                },
                {
                    name: 'uEdgeFalloff',
                    value: uEdgeFalloff,
                    options: { min: 0, max: 1, step: 0.01 },
                },
                { name: 'uNumWaves', value: uNumWaves, options: { min: 1, max: 12, step: 1 } },
                {
                    name: 'uWaveSpacing',
                    value: uWaveSpacing,
                    options: { min: 0.0, max: 0.2, step: 0.001 },
                },
                {
                    name: 'uWaveThickness',
                    value: uWaveThickness,
                    options: { min: 0.0, max: 0.2, step: 0.001 },
                },
                {
                    name: 'uWaveStepAmount',
                    value: uWaveStepAmount,
                    options: { min: 1, max: 20, step: 1 },
                },
                {
                    name: 'uWaveStepOffset',
                    value: uWaveStepOffset,
                    options: { min: 0, max: 1, step: 0.01 },
                },
            ],
            { title: 'Super Bg', expanded: false }
        );
    }
</script>
